import MaterialReactTable from "material-react-table";
import React, { useEffect, useState } from "react";
import axios from "../utils/axios";

const normalizeValueOptions = (options = []) =>
  options.map(
    (option) => ({
      text: option.display_name,
      value: option.value,
    }),
    {}
  );

const normalizeOptions = (options = []) =>
  options.reduce(
    (resultNestedOptions, option) => ({
      ...resultNestedOptions,
      [option.value]: option.display_name,
    }),
    {}
  );

const getExtendedColumns = (columns, options) =>
  columns.map((column) =>
    column.filterVariant === "multi-select" || column.filterVariant === "select"
      ? {
          ...column,
          filterSelectOptions: normalizeValueOptions(
            options[column.accessorKey]
          ),
          selectOptionsLookup: normalizeOptions(options[column.accessorKey]),
        }
      : column
  );

const SimpleDataGrid = ({
  loading,
  columns,
  options = {},
  data = [],
  ...props
}) => (
  <MaterialReactTable
    columns={getExtendedColumns(columns, options)}
    data={data}
    enableColumnActions={false}
    enableColumnFilters={false}
    enablePagination={false}
    enableSorting={false}
    enableBottomToolbar={false}
    enableTopToolbar={false}
    loading={loading}
    {...props}
  />
);

const ServerDataGrid = ({
  columns: nonStableColumns,
  apiUrl,
  dateTimeFilterFields,
  onRowClick,
  getRowId,
  initialState = {},
  ...props
}) => {
  const [columns, setColumns] = useState(nonStableColumns);
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);

  useEffect(() => {
    const getOptions = async () => {
      try {
        const { data } = await axios.options(apiUrl);

        setColumns(getExtendedColumns(columns, data));
      } catch (error) {
        console.log(error);
      }
    };

    const getData = async () => {
      if (!data.length) {
        setIsLoading(true);

        await getOptions();
      } else {
        setIsRefetching(true);
      }

      try {
        const {
          data: { entries, count },
        } = await axios.get(apiUrl, {
          params: {
            searchFilter: JSON.stringify(
              columnFilters
                .filter(
                  (filter) =>
                    !Array.isArray(filter.value) ||
                    (Array.isArray(filter.value) &&
                      filter.value.reduce(
                        (containsValue, filterValue) =>
                          containsValue || filterValue !== undefined,
                        false
                      ))
                )
                .map(({ id: field, value }) => ({
                  field,
                  value,
                  operator: columns.find(
                    (column) => column.accessorKey === field
                  )?.operator,
                }))
            ),
            order_by:
              sorting.length > 0
                ? sorting[0].desc
                  ? `-${sorting[0].id}`
                  : sorting[0].id
                : null,
            pageSize: pagination.pageSize,
            page: pagination.pageIndex + 1,
          },
        });
        setRowCount(count);
        setData(entries);
      } catch (error) {
        setIsError(error.message);
        console.log(error);
      }

      setIsError(false);
      setIsLoading(false);
      setIsRefetching(false);
    };

    getData();
  }, [columnFilters, pagination.pageIndex, pagination.pageSize, sorting]);

  // TODO: Fix table overflow on the middle screen size
  return (
    <MaterialReactTable
      columns={columns}
      data={data}
      getRowId={getRowId}
      initialState={{ showColumnFilters: true, ...initialState }}
      manualFiltering
      manualPagination
      manualSorting
      enableGlobalFilter={false}
      muiToolbarAlertBannerProps={
        isError
          ? {
              color: "error",
              children: "Error loading data",
            }
          : undefined
      }
      onColumnFiltersChange={setColumnFilters}
      onPaginationChange={setPagination}
      onSortingChange={setSorting}
      rowCount={rowCount}
      state={{
        columnFilters,
        isLoading,
        pagination,
        showAlertBanner: isError,
        showProgressBars: isRefetching,
        sorting,
      }}
      {...props}
    />
  );
};

export { SimpleDataGrid, ServerDataGrid };
